import { ROUTES } from "@/constants/routes";
import {
  useSidebarContext,
  withSidebarContext,
} from "@/context/SidebarContext";
import React, { useMemo, useRef, useState } from "react";
import { useLocation, useMatch, useNavigate } from "react-router-dom";

import { useUsageLimitReason } from "@/api/user";
import { useDisplayMode } from "@/hooks/useAppBadge";
import { useBreakpoint } from "@/hooks/useBreakpoint";

import Sidebar from "@/components/Sidebar/Sidebar";
import { usePaneLayoutWidths } from "@/context/PaneLayoutWidthsContext";
import {
  RightPaneExpandContext,
  type RightPaneExpandContextValue,
} from "@/context/RightPaneExpandContext";
import { DropZoneProvider } from "@/dnd/OsFileDropLayer";
import LogoFull from "@/svg/brand/cohesive-logo-full.svg?react";
import { cn } from "@/utils";

import { UsageLimitBanner } from "@/components/billing/UsageLimitBanner";
import { FileConflictResolverProvider } from "@/components/FileConflictResolver";
import { StatusBanner } from "@/components/status/StatusBanner";
import { Flags } from "@/providers/LaunchDarklyProvider";
import { useFlags } from "launchdarkly-react-client-sdk";
import { motion } from "motion/react";
import { LayoutSettingsShell } from "../shared/LayoutSettingsShell";
import { RightPaneRouter } from "../shared/RightPaneRouter";

interface CohesiveLayoutProps {
  children: React.ReactNode;
}

const CohesiveLayout: React.FC<CohesiveLayoutProps> = ({ children }) => {
  const lg = useBreakpoint("lg");
  const location = useLocation();
  const navigate = useNavigate();

  const { isDrawerOpen, setIsDrawerOpen } = useSidebarContext();

  const mainRef = useRef<HTMLDivElement>(null);

  const flags = useFlags<Flags>();
  const { reason } = useUsageLimitReason();

  const isRightPanelOpen = !!location.state?.rightPane?.pathname;

  const displayMode = useDisplayMode();

  const isSession = useMatch(ROUTES.SESSIONS.SESSION);

  const isFileRecordDetail = useMatch(ROUTES.FILE_RECORDS.DETAIL);
  const isHome = useMatch(ROUTES.HOME);
  const isSkillShow = useMatch(ROUTES.SKILLS.SHOW);
  const isSkillNew = useMatch(ROUTES.SKILLS.NEW);
  const isSkillEdit = useMatch(ROUTES.SKILLS.EDIT);
  const isSkillDetail = useMatch(ROUTES.SKILLS.SHOW);
  const isSkillRoot = useMatch(ROUTES.SKILLS.ROOT);

  const isDropZoneEnabled = useMemo(() => {
    return (
      !isSkillShow &&
      !isSkillNew &&
      !isSkillEdit &&
      !isSkillDetail &&
      !isSkillRoot &&
      !isHome
    );
  }, [
    isSkillShow,
    isSkillNew,
    isSkillEdit,
    isSkillDetail,
    isSkillRoot,
    isHome,
  ]);

  const [isRightPanelExpanded, setIsRightPanelExpanded] = useState(false);
  const { rightPanelWidth, setRightPanelWidth } = usePaneLayoutWidths();

  const rightPaneExpandContextValue: RightPaneExpandContextValue = useMemo(
    () => ({
      isExpanded: isRightPanelExpanded,
      rightPanelWidth: isRightPanelOpen ? rightPanelWidth : 0,
      leftPanelWidth: 0,
      leftChatDockPx: 0,
      setLeftChatDockPx: () => {},
      expandToMax: () => {
        setIsRightPanelExpanded(true);
      },
      // Collapsing just drops the expanded render override; the user's stored
      // (dragged) width comes back on its own.
      collapseToDefault: () => {
        setIsRightPanelExpanded(false);
      },
    }),
    [isRightPanelExpanded, isRightPanelOpen, rightPanelWidth],
  );

  const isFullViewport = isFileRecordDetail;

  return (
    <LayoutSettingsShell>
      <FileConflictResolverProvider>
        <>
          {displayMode === "standalone" && (
            <div className="border-t h-[1px] w-dvw absolute top-0 left-0 z-50"></div>
          )}

          <div
            className={cn(
              "absolute top-0 left-0 bottom-0 right-0 bg-n-95/0 z-10 pointer-events-none transition-all duration-300",
              isDrawerOpen && "pointer-events-auto bg-n-95/50 dark:bg-n-00/50",
            )}
            onClick={() => setIsDrawerOpen(false)}
          />

          <div className="h-dvh flex flex-col">
            {!lg && (
              <div className="w-full border-b pt-safe">
                <div className="flex h-[48px] items-center justify-between">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                      className="p-2"
                    >
                      <div className="w-5 h-5 flex flex-col justify-center items-center">
                        <div className="w-4 h-0.5 bg-foreground rounded mb-1"></div>
                        <div className="w-4 h-0.5 bg-foreground rounded mb-1"></div>
                        <div className="w-4 h-0.5 bg-foreground rounded"></div>
                      </div>
                    </button>

                    <button
                      aria-label="Home"
                      onClick={() => navigate(ROUTES.HOME)}
                      className="cursor-pointer"
                    >
                      <LogoFull width="100px" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <StatusBanner className="m-0 z-50" />
            {reason !== "none" && flags.billing && (
              <UsageLimitBanner className="m-0 z-50" />
            )}

            <RightPaneExpandContext.Provider
              value={rightPaneExpandContextValue}
            >
              <div className="flex flex-row grow min-h-0 box-border relative overflow-hidden">
                <Sidebar mode="collapsed" />
                <DropZoneProvider
                  className="relative"
                  enabled={isDropZoneEnabled}
                >
                  <div
                    className={cn(
                      "h-full relative",
                      "transition-all duration-200 flex-1 flex flex-col items-center justify-center align-center overflow-hidden",
                      isHome && "bg-surface-main",
                      isDrawerOpen && "scale-95 opacity-50",
                    )}
                  >
                    <main
                      className={cn(
                        "box-border",
                        "@container",
                        "transition-all duration-200",
                        "scrollbar-hide overflow-auto flex-1 min-h-0",
                        "w-full",
                        isDrawerOpen && "overflow-hidden",
                        !isFullViewport && "px-4 sm:px-[16px] md:px-[32px]",
                        isHome && "pb-0 pt-4 sm:pt-[50px]",
                      )}
                      ref={mainRef}
                    >
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={cn(
                          "box-border relative mx-auto flex flex-col",
                          "h-full",
                          !isSession && !isFullViewport && "max-w-[1100px]",
                        )}
                      >
                        <nav id="navigation-slot" />
                        {children}
                      </motion.div>
                    </main>
                  </div>
                </DropZoneProvider>

                <RightPaneRouter
                  mode="legacy"
                  maxPaneWidth={800}
                  isRightPanelOpen={isRightPanelOpen}
                  onRightPanelWidthChange={setRightPanelWidth}
                  setIsExpanded={setIsRightPanelExpanded}
                  isExpanded={isRightPanelExpanded}
                  leftChatDockPx={0}
                />
              </div>
            </RightPaneExpandContext.Provider>
          </div>
        </>
      </FileConflictResolverProvider>
    </LayoutSettingsShell>
  );
};

export default React.memo(withSidebarContext(CohesiveLayout));
